import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

export const THEMES = ['system', 'light', 'dark'] as const

export type Theme = (typeof THEMES)[number]

export const THEME_KEY = 'porotutu.react-theme'

export const CONTRAST_KEY = 'porotutu.react-contrast'

export const TEXT_SIZES = ['normal', 'large', 'largest'] as const

export type TextSize = (typeof TEXT_SIZES)[number]

export const TEXT_SIZE_KEY = 'porotutu.react-text-size'

export const isTextSize = (value: unknown): value is TextSize =>
  typeof value === 'string' && (TEXT_SIZES as readonly string[]).includes(value)

export const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && (THEMES as readonly string[]).includes(value)

export function stored(): Theme {
  try {
    const value = localStorage.getItem(THEME_KEY)

    return isTheme(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

export function storedContrast(): boolean {
  try {
    return localStorage.getItem(CONTRAST_KEY) === 'high'
  } catch {
    return false
  }
}

export function storedTextSize(): TextSize {
  try {
    const value = localStorage.getItem(TEXT_SIZE_KEY)

    return isTextSize(value) ? value : 'normal'
  } catch {
    return 'normal'
  }
}

export function apply(theme: Theme) {
  const root = document.documentElement

  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function applyContrast(high: boolean) {
  const root = document.documentElement

  if (high) root.setAttribute('data-contrast', 'high')
  else root.removeAttribute('data-contrast')
}

export function applyTextSize(size: TextSize) {
  const root = document.documentElement

  if (size === 'normal') root.removeAttribute('data-text-size')
  else root.setAttribute('data-text-size', size)
}

type ThemeControls = {
  theme: Theme
  setTheme: (next: Theme) => void
  highContrast: boolean
  setHighContrast: (next: boolean) => void
  textSize: TextSize
  setTextSize: (next: TextSize) => void
}

const ThemeContext = createContext<ThemeControls | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setState] = useState<Theme>(stored)
  const [highContrast, setContrastState] = useState<boolean>(storedContrast)
  const [textSize, setTextSizeState] = useState<TextSize>(storedTextSize)

  useEffect(() => {
    apply(theme)
  }, [theme])

  useEffect(() => {
    applyContrast(highContrast)
  }, [highContrast])

  useEffect(() => {
    applyTextSize(textSize)
  }, [textSize])

  const setTheme = useCallback((next: Theme) => {
    apply(next)
    setState(next)

    try {
      localStorage.setItem(THEME_KEY, next)
    } catch {}
  }, [])

  const setHighContrast = useCallback((next: boolean) => {
    applyContrast(next)
    setContrastState(next)

    try {
      localStorage.setItem(CONTRAST_KEY, next ? 'high' : 'normal')
    } catch {}
  }, [])

  const setTextSize = useCallback((next: TextSize) => {
    applyTextSize(next)
    setTextSizeState(next)

    try {
      localStorage.setItem(TEXT_SIZE_KEY, next)
    } catch {}
  }, [])

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, highContrast, setHighContrast, textSize, setTextSize }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeControls {
  const controls = useContext(ThemeContext)

  if (controls === null) throw new Error('useTheme outside ThemeProvider')

  return controls
}
